import {
  createColumn,
  createGroup,
  createRelationship,
  createTable,
  findTable,
  GROUP_COLOR_PALETTE,
  type Column,
  type ColumnSettings,
  type DatabaseType,
  type EnumDef,
  type Group,
  type ParseErrorInfo,
  type Project,
  type RefType,
  type Relationship,
  type Schema,
  type Table,
  type TablePosition,
} from '@hyphen-db-studio/schema-core';
import { parseDbml } from '@hyphen-db-studio/dbml-parser';
import { generateDbml } from '@hyphen-db-studio/dbml-generator';
import { computeAutoLayout } from '@hyphen-db-studio/diagram-engine';
import { create } from 'zustand';
import { useProjectsStore } from './useProjectsStore';

export type SelectionRef =
  | { type: 'table'; id: string }
  | { type: 'group'; id: string }
  | { type: 'relationship'; id: string }
  | { type: 'enum'; id: string }
  | null;

interface HistoryEntry {
  dbmlSource: string;
  schema: Schema;
}

function cloneSchema(schema: Schema): Schema {
  return JSON.parse(JSON.stringify(schema));
}

const HISTORY_LIMIT = 100;

interface SchemaState {
  projectId: string | null;
  projectName: string;
  databaseType: DatabaseType;
  documentation: string;
  dbmlSource: string;
  schema: Schema;
  parseErrors: ParseErrorInfo[];
  selection: SelectionRef;
  dirty: boolean;
  past: HistoryEntry[];
  future: HistoryEntry[];

  loadProject: (project: Project) => void;
  setDbmlSource: (source: string) => void;
  commitSchema: (next: Schema, opts?: { regenerateDbml?: boolean; snapshot?: boolean }) => void;
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;
  setSelection: (sel: SelectionRef) => void;
  rename: (name: string) => void;
  setDocumentation: (doc: string) => void;

  addTable: (name?: string) => Table;
  updateTable: (id: string, patch: Partial<Pick<Table, 'name' | 'note' | 'color'>>) => void;
  deleteTable: (id: string) => void;
  deleteTables: (ids: string[]) => void;
  setTablePosition: (id: string, position: TablePosition, snapshot?: boolean) => void;
  setTablePositions: (positions: Record<string, TablePosition>, snapshot?: boolean) => void;

  addColumn: (tableId: string, name?: string, type?: string) => void;
  updateColumn: (tableId: string, columnId: string, patch: Partial<Column> & { settings?: ColumnSettings }) => void;
  deleteColumn: (tableId: string, columnId: string) => void;

  addRelationship: (fromTableId: string, fromColumnId: string, toTableId: string, toColumnId: string, type?: RefType) => void;
  updateRelationship: (id: string, patch: Partial<Pick<Relationship, 'type' | 'name'>>) => void;
  deleteRelationship: (id: string) => void;

  addGroup: (name?: string, tableIds?: string[]) => Group;
  updateGroup: (id: string, patch: Partial<Pick<Group, 'name' | 'color'>>) => void;
  deleteGroup: (id: string) => void;
  setTableGroup: (tableId: string, groupId: string | null) => void;

  addEnum: (name?: string, values?: string[]) => EnumDef;
  deleteEnum: (id: string) => void;

  autoLayout: () => void;
  save: () => void;
}

function emptySchemaState() {
  return {
    projectId: null as string | null,
    projectName: 'Untitled',
    databaseType: 'postgresql' as DatabaseType,
    documentation: '',
    dbmlSource: '',
    schema: { tables: [], relationships: [], enums: [], groups: [] } as Schema,
    parseErrors: [] as ParseErrorInfo[],
    selection: null as SelectionRef,
    dirty: false,
    past: [] as HistoryEntry[],
    future: [] as HistoryEntry[],
  };
}

export const useSchemaStore = create<SchemaState>((set, get) => ({
  ...emptySchemaState(),

  loadProject: (project) => {
    set({
      projectId: project.id,
      projectName: project.name,
      databaseType: project.databaseType,
      documentation: project.documentation ?? '',
      dbmlSource: project.dbmlSource,
      schema: project.schema,
      parseErrors: [],
      selection: null,
      dirty: false,
      past: [],
      future: [],
    });
  },

  setDbmlSource: (source) => {
    const { schema, errors } = parseDbml(source);
    const prev = { dbmlSource: get().dbmlSource, schema: get().schema };
    set((state) => ({
      dbmlSource: source,
      schema,
      parseErrors: errors,
      dirty: true,
      past: [...state.past, prev].slice(-HISTORY_LIMIT),
      future: [],
    }));
  },

  commitSchema: (next, opts = {}) => {
    const { regenerateDbml = true, snapshot = true } = opts;
    const prev = { dbmlSource: get().dbmlSource, schema: get().schema };
    const dbmlSource = regenerateDbml ? generateDbml(next) : get().dbmlSource;
    set((state) => ({
      schema: next,
      dbmlSource,
      dirty: true,
      past: snapshot ? [...state.past, prev].slice(-HISTORY_LIMIT) : state.past,
      future: snapshot ? [] : state.future,
    }));
  },

  undo: () => {
    const { past } = get();
    if (!past.length) return;
    const previous = past[past.length - 1];
    const current = { dbmlSource: get().dbmlSource, schema: get().schema };
    set({
      dbmlSource: previous.dbmlSource,
      schema: previous.schema,
      past: past.slice(0, -1),
      future: [current, ...get().future].slice(0, HISTORY_LIMIT),
      dirty: true,
    });
  },

  redo: () => {
    const { future } = get();
    if (!future.length) return;
    const next = future[0];
    const current = { dbmlSource: get().dbmlSource, schema: get().schema };
    set({
      dbmlSource: next.dbmlSource,
      schema: next.schema,
      future: future.slice(1),
      past: [...get().past, current].slice(-HISTORY_LIMIT),
      dirty: true,
    });
  },

  canUndo: () => get().past.length > 0,
  canRedo: () => get().future.length > 0,

  setSelection: (sel) => set({ selection: sel }),

  rename: (name) => set({ projectName: name, dirty: true }),
  setDocumentation: (doc) => set({ documentation: doc, dirty: true }),

  addTable: (name) => {
    const schema = get().schema;
    const table = createTable(name ?? `table_${schema.tables.length + 1}`);
    const next = { ...schema, tables: [...schema.tables, table] };
    get().commitSchema(next);
    get().setSelection({ type: 'table', id: table.id });
    return table;
  },

  updateTable: (id, patch) => {
    const schema = get().schema;
    const next = { ...schema, tables: schema.tables.map((t) => (t.id === id ? { ...t, ...patch } : t)) };
    get().commitSchema(next);
  },

  deleteTable: (id) => {
    const schema = get().schema;
    const next: Schema = {
      tables: schema.tables.filter((t) => t.id !== id),
      relationships: schema.relationships.filter((r) => r.fromTableId !== id && r.toTableId !== id),
      enums: schema.enums,
      groups: schema.groups.map((g) => ({ ...g, tableIds: g.tableIds.filter((tid) => tid !== id) })),
    };
    get().commitSchema(next);
    if (get().selection?.type === 'table' && get().selection?.id === id) get().setSelection(null);
  },

  deleteTables: (ids) => {
    const drop = new Set(ids);
    const schema = get().schema;
    const next: Schema = {
      tables: schema.tables.filter((t) => !drop.has(t.id)),
      relationships: schema.relationships.filter((r) => !drop.has(r.fromTableId) && !drop.has(r.toTableId)),
      enums: schema.enums,
      groups: schema.groups.map((g) => ({ ...g, tableIds: g.tableIds.filter((tid) => !drop.has(tid)) })),
    };
    get().commitSchema(next);
    const sel = get().selection;
    if (sel?.type === 'table' && drop.has(sel.id)) get().setSelection(null);
  },

  setTablePosition: (id, position, snapshot = false) => {
    const schema = get().schema;
    const next = { ...schema, tables: schema.tables.map((t) => (t.id === id ? { ...t, position } : t)) };
    get().commitSchema(next, { regenerateDbml: false, snapshot });
  },

  setTablePositions: (positions, snapshot = false) => {
    const schema = get().schema;
    const next = {
      ...schema,
      tables: schema.tables.map((t) => (positions[t.id] ? { ...t, position: positions[t.id] } : t)),
    };
    get().commitSchema(next, { regenerateDbml: false, snapshot });
  },

  addColumn: (tableId, name, type) => {
    const schema = get().schema;
    const table = findTable(schema, tableId);
    if (!table) return;
    const column = createColumn(name ?? `column_${table.columns.length + 1}`, type ?? 'varchar');
    const next = {
      ...schema,
      tables: schema.tables.map((t) => (t.id === tableId ? { ...t, columns: [...t.columns, column] } : t)),
    };
    get().commitSchema(next);
  },

  updateColumn: (tableId, columnId, patch) => {
    const schema = get().schema;
    const next = {
      ...schema,
      tables: schema.tables.map((t) =>
        t.id === tableId
          ? {
              ...t,
              columns: t.columns.map((c) =>
                c.id === columnId ? { ...c, ...patch, settings: { ...c.settings, ...(patch.settings ?? {}) } } : c,
              ),
            }
          : t,
      ),
    };
    get().commitSchema(next);
  },

  deleteColumn: (tableId, columnId) => {
    const schema = get().schema;
    const next: Schema = {
      tables: schema.tables.map((t) => (t.id === tableId ? { ...t, columns: t.columns.filter((c) => c.id !== columnId) } : t)),
      relationships: schema.relationships.filter((r) => r.fromColumnId !== columnId && r.toColumnId !== columnId),
      enums: schema.enums,
      groups: schema.groups,
    };
    get().commitSchema(next);
  },

  addRelationship: (fromTableId, fromColumnId, toTableId, toColumnId, type = '>') => {
    const schema = get().schema;
    const rel = createRelationship(fromTableId, fromColumnId, toTableId, toColumnId, type);
    const next = { ...schema, relationships: [...schema.relationships, rel] };
    get().commitSchema(next);
  },

  updateRelationship: (id, patch) => {
    const schema = get().schema;
    const next = { ...schema, relationships: schema.relationships.map((r) => (r.id === id ? { ...r, ...patch } : r)) };
    get().commitSchema(next);
  },

  deleteRelationship: (id) => {
    const schema = get().schema;
    const next = { ...schema, relationships: schema.relationships.filter((r) => r.id !== id) };
    get().commitSchema(next);
  },

  addGroup: (name, tableIds = []) => {
    const schema = get().schema;
    const color = GROUP_COLOR_PALETTE[schema.groups.length % GROUP_COLOR_PALETTE.length];
    const group = createGroup(name ?? `Group ${schema.groups.length + 1}`, tableIds, color);
    const next: Schema = {
      ...schema,
      groups: [...schema.groups, group],
      tables: schema.tables.map((t) => (tableIds.includes(t.id) ? { ...t, groupId: group.id } : t)),
    };
    get().commitSchema(next);
    get().setSelection({ type: 'group', id: group.id });
    return group;
  },

  updateGroup: (id, patch) => {
    const schema = get().schema;
    const next = { ...schema, groups: schema.groups.map((g) => (g.id === id ? { ...g, ...patch } : g)) };
    get().commitSchema(next);
  },

  deleteGroup: (id) => {
    const schema = get().schema;
    const next: Schema = {
      ...schema,
      groups: schema.groups.filter((g) => g.id !== id),
      tables: schema.tables.map((t) => (t.groupId === id ? { ...t, groupId: null } : t)),
    };
    get().commitSchema(next);
  },

  setTableGroup: (tableId, groupId) => {
    const schema = get().schema;
    const next: Schema = {
      ...schema,
      tables: schema.tables.map((t) => (t.id === tableId ? { ...t, groupId } : t)),
      groups: schema.groups.map((g) => ({
        ...g,
        tableIds:
          g.id === groupId
            ? [...new Set([...g.tableIds, tableId])]
            : g.tableIds.filter((id) => id !== tableId),
      })),
    };
    get().commitSchema(next);
  },

  addEnum: (name, values = []) => {
    const schema = get().schema;
    const en: EnumDef = {
      id: `enum_${schema.enums.length + 1}_${Date.now().toString(36)}`,
      name: name ?? `Enum${schema.enums.length + 1}`,
      values: values.map((v, i) => ({ id: `ev_${i}_${Date.now().toString(36)}`, name: v })),
    };
    const next = { ...schema, enums: [...schema.enums, en] };
    get().commitSchema(next);
    return en;
  },

  deleteEnum: (id) => {
    const schema = get().schema;
    const next = { ...schema, enums: schema.enums.filter((e) => e.id !== id) };
    get().commitSchema(next);
  },

  autoLayout: () => {
    const schema = get().schema;
    const positions = computeAutoLayout(schema);
    const next = {
      ...schema,
      tables: schema.tables.map((t) => (positions[t.id] ? { ...t, position: positions[t.id] } : t)),
    };
    get().commitSchema(next, { regenerateDbml: false });
  },

  save: () => {
    const state = get();
    if (!state.projectId) return;
    const project: Project = {
      id: state.projectId,
      name: state.projectName,
      databaseType: state.databaseType,
      dbmlSource: state.dbmlSource,
      schema: cloneSchema(state.schema),
      documentation: state.documentation,
      createdAt: useProjectsStore.getState().get(state.projectId)?.createdAt ?? new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    useProjectsStore.getState().upsert(project);
    set({ dirty: false });
  },
}));
